import { SolidStreamingMarkdown, createSolidStreamingMarkdown } from "@storytell/markdown";
import type { StreamingChatId, StreamingMessage, StreamingOperation, createStreamingChat } from "@storytell/streaming";
import type { IconTypes } from "solid-icons";
import { TbCpu, TbFileTextAi, TbSearch, TbTool, TbWifi, TbWorld } from "solid-icons/tb";
import { For, type JSX, Show, createMemo } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";

const matchesData = {
  user_kb_search_v1: {
    title: "Internal Knowledge Base Search",
    description: "Searches your organization's internal knowledge base for relevant information.",
    icon: TbSearch,
    type: "input",
  },
  kb_search: {
    title: "Knowledge Base Search",
    description: "Searches knowledge base for documents and structured data",
    icon: TbSearch,
    type: "input",
  },
  web_search: {
    title: "Public Web Search",
    description: "Performs a search on the public web to find up-to-date external information.",
    icon: TbWorld,
    type: "input",
  },
  web_search_v1: {
    title: "Public Web Search",
    description: "Performs a search on the public web to find up-to-date external information.",
    icon: TbWorld,
    type: "input",
  },
  artifact_generation: {
    title: "Generate Artifacts",
    description: "Creates and outputs documents, files, or other artifacts based on your request.",
    icon: TbFileTextAi,
    type: "output",
  },
  artifact_v1: {
    title: "Generate Artifacts",
    description: "Creates and outputs documents, files, or other artifacts based on your request.",
    icon: TbFileTextAi,
    type: "output",
  },
};
export type ToolMatchKey = keyof typeof matchesData;
// Regex to match all the tool matches
export const ToolMatchesRegex = new RegExp(
  Object.keys(matchesData)
    .map((key) => key)
    .join("|"),
  "g",
);

export const ToolMatchesSection = (props: {
  chatId: StreamingChatId;
}) => {
  const wire = useWire();
  const stream = wire.stream;
  const chat = () => stream.chat.get(props.chatId);
  const messages = () =>
    chat()
      ?.messages?.map((messageId) => stream.message.get(messageId))
      .filter((m): m is StreamingMessage => !!m);

  const matches = createMemo(() => {
    const matches = new Set<ToolMatchKey>();

    for (const message of messages() ?? []) {
      const operations = message.operations
        .map((operationId) => stream.operation.get(operationId))
        .filter((o): o is StreamingOperation => !!o);

      for (const op of operations) {
        const match = matchesData[op.activity as ToolMatchKey];
        if (match) {
          matches.add(op.activity as ToolMatchKey);
        }
      }
    }
    return Array.from(matches);
  });

  return (
    <Show when={matches()?.length > 0}>
      <ToolMatchesHighlight matches={matches().map((match) => ({ match, line: "" }))} />
    </Show>
  );
};

export const ToolMatchesHighlight = (props: { matches: { match: ToolMatchKey; line: string }[] }) => {
  const inputs = () =>
    props.matches
      .filter((match) => matchesData[match.match].type === "input")
      .map((match) => ({
        ...matchesData[match.match],
        line: match.line,
      }));
  const outputs = () =>
    props.matches
      .filter((match) => matchesData[match.match].type === "output")
      .map((match) => ({
        ...matchesData[match.match],
        line: match.line,
      }));

  return (
    <Show when={inputs().length > 0 || outputs().length > 0}>
      <div class="ml-1 mb-3 rounded-xl">
        <h3 class="text-base font-semibold capitalize flex items-center gap-1">
          <TbTool class="opacity-80" />
          Tools I'm choosing for this job:
        </h3>

        <Show when={inputs().length > 0}>
          <InputOutputCard
            type="input"
            title="Data Ingestion Sources:"
            icon={TbCpu}
            requestMoreUrl="https://go.storytell.ai/ingest-tools"
          >
            <For each={inputs()}>
              {(item) => (
                <InputOutputToolItem
                  class="border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-700 dark:bg-blue-900 dark:text-blue-300"
                  icon={item.icon}
                  title={item.title}
                  line={item.line}
                  description={item.description}
                />
              )}
            </For>
          </InputOutputCard>
        </Show>

        <Show when={outputs().length > 0}>
          <InputOutputCard
            type="output"
            title="Resulting Output:"
            icon={TbWifi}
            requestMoreUrl="https://go.storytell.ai/action-tools"
          >
            <For each={outputs()}>
              {(item) => (
                <InputOutputToolItem
                  class="border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
                  icon={item.icon}
                  title={item.title}
                  line={item.line}
                  description={item.description}
                />
              )}
            </For>
          </InputOutputCard>
        </Show>
      </div>
    </Show>
  );
};

const InputOutputToolItem = (props: {
  icon: IconTypes;
  title: string;
  description: string;
  line: string;
  class?: string;
}) => {
  return (
    <div class="ml-2 mt-2">
      <div class="flex items-center gap-2 text-base font-semibold mb-2">
        <span
          class={twMerge(
            "grid place-content-center bg-panel rounded-full size-6 border border-primary-border",
            props.class,
          )}
        >
          <props.icon class="size-4" />
        </span>
        <span>{props.title}</span>
      </div>

      <div class="ml-8">
        <p class="text-sm text-secondary-text">{props.description}</p>
      </div>
      {/* <Show when={props.line}>
        <div class="ml-4">
          <LineRenderer line={props.line} />
        </div>
      </Show> */}
    </div>
  );
};

const InputOutputCard = (props: {
  type: "input" | "output";
  title: string;
  children: JSX.Element;
  icon: IconTypes;
  requestMoreUrl: string;
}) => {
  return (
    <div class="flex flex-col gap-2 mt-3 bg-panel hover:bg-panel-hover border border-primary-border p-3 rounded-xl">
      <div class="flex items-baseline justify-between gap-2">
        <h4 class="text-base font-semibold flex items-center gap-1">
          <props.icon class="opacity-80" classList={{ "rotate-45": props.type === "output" }} />

          {props.title}
        </h4>
        <a href={props.requestMoreUrl} rel="noreferrer" target="_blank" class="underline text-sm">
          {props.type === "input" ? "+ more sources" : "+ more destinations"}
        </a>
      </div>
      {props.children}
    </div>
  );
};

const LineRenderer = (props: { line: string }) => {
  const markdownStreamer = createSolidStreamingMarkdown(props.line);

  return (
    <div class="text-sm max-w-none">
      <SolidStreamingMarkdown
        doc={markdownStreamer.doc}
        options={{
          classes: {
            document: "wrap-break-word whitespace-break-spaces",
            heading: (node) => {
              switch (node.level) {
                case 1:
                  return "font-medium text-lg mb-2 mt-3";
                case 2:
                  return "font-medium text-base mb-2 mt-3";
                case 3:
                  return "font-medium text-sm mb-2 mt-3";
                default:
                  return "font-medium text-sm mb-2 mt-3";
              }
            },
            paragraph: "mb-2",
            list: (node) => {
              switch (node.listData?.type) {
                case "ordered":
                  return "list-decimal pl-4";
                case "bullet":
                  return "list-disc pl-4";
                default:
                  return "";
              }
            },
            item: "mb-1",
            code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-xs",
            blockQuote: "border-l-2 border-blue-100 dark:border-blue-700 pl-2 py-1 my-1",
            link: "font-medium text-primary-text underline underline-offset-2",
          },
        }}
      />
    </div>
  );
};
